import { StepperSelectionEvent } from "@angular/cdk/stepper";
import { ChangeDetectionStrategy, Component, inject, OnDestroy } from "@angular/core";
import { CreateEventFormStore } from "./_services/create-event-form-store.service";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { EventForm } from "../_models/event-form.model";
import { maxDaysPastValidator } from "../validators/created-validator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { AuthService } from "src/app/services/auth.service";
import { SebbiesDialogComponent } from "src/app/components/sebbies-dialog/sebbies-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { formatDate } from "@angular/common";

const MAX_DAYS_IN_PAST = 2;

@Component({
  selector: 'new-event-form',
  templateUrl: './new-event-form.component.html',
  styleUrls: ['./new-event-form.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewEventFormComponent implements OnDestroy{
  public eventFormStore = inject(CreateEventFormStore);

  public authService = inject(AuthService);

  public router = inject(Router);

  public materialSnackbar = inject(MatSnackBar);

  public dialog = inject(MatDialog);

  private fb = inject(FormBuilder);

  public streappFormGroup: FormGroup<EventForm>;

  public constructor() {
    const now = new Date();
    var formattedDate = formatDate(now, 'yyyy-MM-dd', 'en');
    formattedDate += 'T' + formatDate(new Date(), 'HH:mm', 'en');

    this.eventFormStore.streappEvent().created = now;

    this.streappFormGroup = this.fb.group({
      detailsForm: this.fb.group({
        titel: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(150)]),
        beschrijving: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(5000)]),
        created: this.fb.nonNullable.control(formattedDate, [Validators.required, maxDaysPastValidator(MAX_DAYS_IN_PAST)]),
      }),
      additionalDetailsForm: this.fb.group({
        privacyEnabled: this.fb.nonNullable.control(undefined, Validators.required),
        emergencyServicesPresent: this.fb.nonNullable.control(undefined, Validators.required),
        highImpactEvent: this.fb.nonNullable.control(undefined, Validators.required),
        helpFunctionForm: this.fb.group({
          victimsOrPersonsVisible: this.fb.nonNullable.control(false),
          minorsVisible: this.fb.nonNullable.control(false),
          emergencyRespondersOrBystandersWantExclusion: this.fb.nonNullable.control(false),
          suspectVisible: this.fb.nonNullable.control(false),
          licensePlatesVisible: this.fb.nonNullable.control(false),
        })
      })
    });
  }
  
  ngOnDestroy(): void {
    this.eventFormStore.reset();
  }

  public onStepChange(event: StepperSelectionEvent) {
    this.eventFormStore.setStep(event.selectedIndex);

    if (this.eventFormStore.currentStep() === 1) {
      // trigger window resize event to make sure the map is rendered correctly when navigating back to the location step      
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    }
  }

  public nextStep() {
    this.eventFormStore.setStep((this.eventFormStore.currentStep() ?? 0) + 1);
  }

  public previousStep() {
    if (this.eventFormStore.currentStep() === 3 && this.eventFormStore.stepCategoryValid()) {
      this.eventFormStore.stepCategoryValid.set(false);
    }
    else {
      this.eventFormStore.setStep((this.eventFormStore.currentStep() ?? 0) - 1);
    }
  }

  public async createEvent(): Promise<void> {
    try {
      const id = await this.eventFormStore.createEvent();
      this.materialSnackbar.open('Streapp succesvol aangemaakt!', 'Ok', { duration: 3000 });

      // Open Sebbies dialog after completion
      this.dialog.open(SebbiesDialogComponent, {
        autoFocus: false,
      });

      this.router.navigate(['detail', id]);
    } catch (error) {
      console.error('Error creating event:', error);
      this.materialSnackbar.open(`Fout bij aanmaken Streapp. Probeer het nogmaals.`,
        'Ok',
        {
          duration: 5000,
        });
    }
  }
}
